(() => {
  const root = document.querySelector('[data-direct-benefits]');
  if (!root) return;

  const ready = root.querySelector('[data-state-ready]');
  const auth = root.querySelector('[data-state-auth]');
  const success = root.querySelector('[data-state-success]');
  const claimButton = root.querySelector('[data-direct-claim]');
  const retryButton = root.querySelector('[data-direct-retry]');
  const login = root.querySelector('[data-direct-login]');
  const error = root.querySelector('[data-direct-error]');
  const title = root.querySelector('[data-direct-title]');
  const receipt = root.querySelector('[data-direct-receipt]');
  const progress = root.querySelector('[data-direct-progress]');
  const progressKicker = root.querySelector('[data-direct-progress-kicker]');
  const progressTitle = root.querySelector('[data-direct-progress-title]');
  const progressCopy = root.querySelector('[data-direct-progress-copy]');
  const progressLink = root.querySelector('[data-direct-progress-link]');
  const claimButtonLabel = claimButton.textContent;
  const campaign = root.dataset.campaign;
  const intentEndpoint = root.dataset.intentEndpoint;
  const claimEndpoint = root.dataset.claimEndpoint;
  const eventEndpoint = root.dataset.eventEndpoint;
  const v25 = root.dataset.directVersion === 'v25';
  const offerDestinations = {
    custom_11: root.dataset.customOfferUrl,
    hp5: root.dataset.hp5OfferUrl,
    maker: root.dataset.makerOfferUrl,
    // The tier is a cart benefit, so continue into the relevant DTF entry.
    dtf_volume: root.dataset.customOfferUrl,
  };
  const pendingOfferKey = 'arteefy-direct-pending-offer';
  const attributionKey = 'arteefy-direct-attribution';
  let busy = false;

  const offerNames = {
    custom_11: 'your $1.49 print price',
    hp5: 'your 50% off HP5 offer',
    maker: 'your 20 Maker designs',
    dtf_volume: 'your automatic DTF savings',
  };

  const showProgress = (offer, phase = 'prepare', destination = '') => {
    if (!v25 || !progress) return;
    const selected = validOffer(offer);
    const name = offerNames[selected] || 'your Direct benefits';
    progressKicker.textContent = phase === 'ready' ? '✓ DIRECT ACCESS ACTIVE' : 'YOUR DIRECT ACCESS';
    progressTitle.textContent = phase === 'activate' ? `Activating ${name}…`
      : phase === 'ready' ? `Opening ${name}…`
        : `Saving ${name}…`;
    progressCopy.textContent = phase === 'activate'
      ? 'Your email is verified. We’re confirming your account benefits now.'
      : phase === 'ready'
        ? 'Everything is ready. You’ll continue automatically.'
        : 'Next, verify one email. We’ll bring you straight back to this choice.';
    if (progressLink) {
      progressLink.hidden = !destination;
      if (destination) progressLink.href = destination;
    }
    progress.hidden = false;
    progress.setAttribute('aria-busy', phase === 'ready' ? 'false' : 'true');
  };

  const hideProgress = () => {
    if (!progress) return;
    progress.hidden = true;
    progress.setAttribute('aria-busy', 'false');
    if (progressLink) progressLink.hidden = true;
  };

  const flowId = () => {
    const key = 'arteefy-direct-flow-id';
    let value = sessionStorage.getItem(key);
    if (!value) {
      value = (crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`).slice(0, 64);
      sessionStorage.setItem(key, value);
    }
    return value;
  };

  const attribution = () => {
    const query = new URL(location.href).searchParams;
    const allowed = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'card_batch'];
    const current = Object.fromEntries(allowed.map((key) => [key, (query.get(key) || '').slice(0, 48)]).filter(([, value]) => value));
    if (Object.keys(current).length) {
      sessionStorage.setItem(attributionKey, JSON.stringify({ values: current, savedAt: Date.now() }));
      return current;
    }
    try {
      const saved = JSON.parse(sessionStorage.getItem(attributionKey) || '{}');
      if (Date.now() - Number(saved.savedAt) <= 24 * 60 * 60 * 1000 && saved.values) return saved.values;
    } catch {
      sessionStorage.removeItem(attributionKey);
    }
    return {};
  };

  const track = (type, extra = {}) => {
    if (!eventEndpoint) return;
    fetch(eventEndpoint, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({ type, campaign, extra: { flow_id: flowId(), ...attribution(), ...extra } }),
    }).catch(() => {});
  };

  const show = (state, { focus = true } = {}) => {
    ready.hidden = state !== 'ready';
    auth.hidden = state !== 'auth';
    success.hidden = state !== 'success';
    if (focus && state === 'auth') auth.focus();
    if (focus && state === 'success') success.focus();
  };

  const clearCallback = () => {
    const url = new URL(location.href);
    url.searchParams.delete('claim');
    url.searchParams.delete('intent');
    url.searchParams.delete('offer');
    url.searchParams.delete('c');
    url.searchParams.delete('o');
    history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
  };

  const validOffer = (offer) => (
    v25 && typeof offer === 'string' && offerDestinations[offer] ? offer : ''
  );

  const rememberOffer = (offer) => {
    const selected = validOffer(offer);
    if (!selected) return '';
    sessionStorage.setItem(pendingOfferKey, JSON.stringify({ offer: selected, savedAt: Date.now() }));
    return selected;
  };

  const recalledOffer = () => {
    try {
      const saved = JSON.parse(sessionStorage.getItem(pendingOfferKey) || '{}');
      if (Date.now() - Number(saved.savedAt) > 30 * 60 * 1000) {
        sessionStorage.removeItem(pendingOfferKey);
        return '';
      }
      return validOffer(saved.offer);
    } catch {
      sessionStorage.removeItem(pendingOfferKey);
      return '';
    }
  };

  const reset = () => {
    busy = false;
    claimButton.disabled = false;
    claimButton.textContent = claimButtonLabel;
  };

  const fetchJson = async (url, body) => {
    const response = await fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    const data = await response.json().catch(() => ({}));
    return { response, data };
  };

  const loginUrl = (intent, offer = '') => {
    const url = new URL(root.dataset.loginUrl, location.origin);
    const returnUrl = new URL(root.dataset.returnPath || '/direct', location.origin);
    const offerCodes = { custom_11: 'p', hp5: 'h', maker: 'm', dtf_volume: 'v' };
    // Attribution is retained in same-tab sessionStorage.  The OTP return URL
    // contains only a short, one-time server-backed handle and a one-letter
    // destination code—never email, customer data, or the old long signature.
    attribution();
    returnUrl.searchParams.set('c', intent);
    const code = offerCodes[validOffer(offer)];
    if (code) returnUrl.searchParams.set('o', code);
    url.searchParams.set('return_to', `${returnUrl.pathname}${returnUrl.search}${returnUrl.hash}`);
    return url.toString();
  };

  const fail = (code) => {
    const copy = {
      claim_intent_expired: 'Your secure link expired. Please try again.',
      claim_intent_invalid: 'We could not verify this request. Please start again.',
      direct_receipt_incomplete: 'We could not confirm your Direct benefits. Nothing was reported as complete; please retry.',
    };
    error.textContent = copy[code] || 'Direct benefits are temporarily unavailable. Please try again shortly.';
    error.hidden = false;
    hideProgress();
    clearCallback();
    show('ready');
    root.querySelector('#direct-access')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    reset();
  };

  const continueToOffer = (offer) => {
    const destination = offerDestinations[validOffer(offer)];
    if (!destination) return;
    track('direct_offer_continue', { offer });
    showProgress(offer, 'ready', destination);
    window.setTimeout(() => window.location.assign(destination), 700);
  };

  const complete = async (intent, offer = '') => {
    const { response, data } = await fetchJson(claimEndpoint, { campaign, intent });
    if (data.error === 'not_logged_in') {
      login.href = loginUrl(intent, offer);
      const selectedOffer = validOffer(offer);
      track('claim_auth_start', { intent_present: true, offer: selectedOffer || 'general' });
      // A benefit-card click already expresses a clear destination intent.
      // Send first-time package customers straight to email account creation
      // instead of making them click a second, redundant sign-in button.
      if (selectedOffer) {
        track('claim_auth_redirect', { offer: selectedOffer });
        window.location.assign(login.href);
        return;
      }
      show('auth');
      busy = false;
      return;
    }
    const tags = Array.isArray(data.customer_tags) ? data.customer_tags : [];
    const balance = Number(data.after ?? data.free_left);
    if (!response.ok || data.ok !== true) throw new Error(data.error || data.detail || 'claim_unavailable');
    if (!tags.includes('direct-customer') || !Number.isFinite(balance)) throw new Error('direct_receipt_incomplete');

    title.textContent = 'Your Direct prices are ready';
    receipt.textContent = data.claimed
      ? `20 free Maker designs were added. You now have ${balance} available.`
      : `Your Direct pricing is active. Your Maker balance remains ${balance} designs.`;
    const destination = offerDestinations[validOffer(offer)];
    if (destination) {
      receipt.textContent += ` Taking you to ${offer === 'hp5' ? 'your HP5 offer' : offer === 'maker' ? 'Maker' : 'your Direct DTF order'}…`;
    }
    error.hidden = true;
    show('success');
    track('claim_result', { result: data.claimed ? 'claimed' : 'already_claimed', after: balance, direct_tag: true, offer: validOffer(offer) || 'general' });
    clearCallback();
    busy = false;
    sessionStorage.removeItem(pendingOfferKey);
    continueToOffer(offer);
  };

  const begin = async (intent = '', offer = '') => {
    if (busy) return;
    const selectedOffer = validOffer(offer);
    showProgress(selectedOffer, intent ? 'activate' : 'prepare');
    busy = true;
    claimButton.disabled = true;
    claimButton.textContent = intent ? 'Activating your benefits…' : 'Preparing secure access…';
    error.hidden = true;
    try {
      let currentIntent = intent;
      if (!currentIntent) {
        track('claim_cta', { offer: selectedOffer || 'general' });
        const { response, data } = await fetchJson(intentEndpoint, { campaign });
        if (!response.ok || data.ok !== true || !data.intent) throw new Error(data.error || 'claim_unavailable');
        currentIntent = data.intent;
        if (!data.authenticated) {
          login.href = loginUrl(currentIntent, selectedOffer);
          track('claim_auth_start', { intent_present: true, offer: selectedOffer || 'general' });
          if (selectedOffer) {
            track('claim_auth_redirect', { offer: selectedOffer });
            window.location.assign(login.href);
            return;
          }
          hideProgress();
          show('auth');
          busy = false;
          return;
        }
      }
      await complete(currentIntent, selectedOffer);
    } catch (claimError) {
      track('claim_result', { result: 'error', error: String(claimError?.message || '').slice(0, 48) });
      fail(claimError?.message || 'claim_unavailable');
    }
  };

  claimButton.addEventListener('click', () => begin('', recalledOffer()));
  retryButton?.addEventListener('click', () => { show('ready'); reset(); });
  root.querySelectorAll('[data-direct-offer]').forEach((link) => {
    link.addEventListener('click', (event) => {
      const offer = validOffer(String(link.dataset.directOffer || ''));
      track('direct_offer_click', {
        offer: String(link.dataset.directOffer || '').slice(0, 48),
        direct_active: root.dataset.active === 'true',
      });
      // V2.5 benefits are explicit activation intents.  The no-JS href stays
      // as a safe scroll fallback, but JS keeps the selected benefit through
      // account creation and sends the customer straight to it afterward.
      if (offer && root.dataset.active !== 'true') {
        event.preventDefault();
        begin('', rememberOffer(offer));
      }
    });
  });
  track('claim_landing', { already_active: root.dataset.active === 'true' });

  const query = new URL(location.href).searchParams;
  const offerCodes = { p: 'custom_11', h: 'hp5', m: 'maker', v: 'dtf_volume' };
  const returnedIntent = query.get('c') || (query.get('claim') === '1' ? query.get('intent') : '');
  // Shopify officially preserves a relative return_to. Session storage is a
  // second same-tab recovery path in case an account flow strips its query.
  const returnedOffer = validOffer(offerCodes[query.get('o')] || query.get('offer') || '') || recalledOffer();
  if (returnedIntent) {
    // Remove the bearer handle from the address bar before any network call or
    // visual transition. The in-memory value remains available to ``begin``.
    clearCallback();
    showProgress(returnedOffer, 'activate');
    track('claim_auth_callback', { intent_present: true, offer: returnedOffer || 'general' });
    begin(returnedIntent, returnedOffer);
  } else if (root.dataset.active === 'true') {
    sessionStorage.removeItem(pendingOfferKey);
    title.textContent = 'Your Direct prices are ready';
    receipt.textContent = 'This account already has Direct customer pricing.';
    // Passive recognition is not a user action: keep their viewport and focus.
    show('success', { focus: false });
  }
})();
